import CustomEvent from 'custom-event';

class Statements {
  constructor(options = {}) {
    this.element = options.element;
    this.allPlansCheckbox = this.element.querySelector('[name="statements-plans-all"]');
    this.planCheckboxes = Array.from(
      this.element.querySelectorAll('[name="statements-plans"]'),
    );
    this.statementRows = Array.from(
      this.element.querySelectorAll('tbody tr[data-plan-ids]'),
    );
    this.titleTeasers = Array.from(
      this.element.querySelectorAll('.org-statements__title'),
    );
    this.descriptionTeasers = Array.from(
      this.element.querySelectorAll('.org-statements__description'),
    );
    this.yearFilter = this.element.querySelector('.org-statements__year-filter');
    this.planFilter = this.element.querySelector('.org-statements__plan-filter');
    this.showAllPlansButton = this.element.querySelector('.org-statements__show-all');
    this.planQueryParameter = 'plan';
    this.yearFilterObserver = null;
  }

  init() {
    this.initTitleTeasers();
    this.initDescriptionTeasers();

    if (!this.hasPlanFilterElements()) {
      return;
    }

    this.setInitialState();
    this.bindAllPlansCheckbox();
    this.bindPlanCheckboxes();
    this.bindShowAllPlansButton();
    this.bindYearFilter();
  }

  hasPlanFilterElements() {
    return this.allPlansCheckbox && this.planCheckboxes.length > 0;
  }

  initTitleTeasers() {
    this.titleTeasers.forEach((title) => {
      const maxLength = parseInt(title.dataset.maxLength, 10) || 200;
      const fullText = title.textContent.trim();

      if (fullText.length <= maxLength) {
        return;
      }

      /* eslint-disable-next-line no-param-reassign */
      title.textContent = `${fullText.substring(0, maxLength)} ...`;
    });
  }

  initDescriptionTeasers() {
    this.descriptionTeasers.forEach((description) => {
      const previewElement = description.querySelector('.org-statements__description-preview');
      const fullTextElement = description.querySelector('.org-statements__description-full-text');
      const moreButton = description.querySelector('.org-statements__description-more');

      if (!previewElement || !fullTextElement || !moreButton) {
        return;
      }

      const maxLength = parseInt(description.dataset.maxLength, 10) || 200;
      const fullText = fullTextElement.textContent.trim();

      if (fullText.length <= maxLength) {
        fullTextElement.hidden = true;
        return;
      }

      previewElement.textContent = `${fullText.substring(0, maxLength)} ...`;
      moreButton.hidden = false;
      moreButton.setAttribute('aria-expanded', 'false');

      moreButton.addEventListener('click', () => {
        previewElement.hidden = true;
        fullTextElement.hidden = false;
        moreButton.setAttribute('aria-expanded', 'true');
        moreButton.hidden = true;
      });
    });
  }

  setInitialState() {
    this.applyInitialPlansFromUrl();
    this.updateStatementVisibility();
    this.updateShowAllPlansButton();
  }

  bindAllPlansCheckbox() {
    this.allPlansCheckbox.addEventListener('change', () => {
      this.setAllPlansChecked(this.allPlansCheckbox.checked);
      this.updateStatementVisibility();
      this.updateUrlFromActivePlans();
    });
  }

  bindPlanCheckboxes() {
    this.planCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        this.syncAllPlansCheckbox();
        this.updateStatementVisibility();
        this.updateUrlFromActivePlans();
      });
    });
  }

  bindShowAllPlansButton() {
    if (!this.planFilter || !this.showAllPlansButton) {
      return;
    }

    this.showAllPlansButton.addEventListener('click', () => {
      this.toggleAllPlans();
    });
  }

  updateShowAllPlansButton() {
    if (this.showAllPlansButton) {
      this.showAllPlansButton.hidden = this.planCheckboxes.length <= 4;
      this.updateShowAllPlansButtonState();
    }
  }

  updateShowAllPlansButtonState() {
    const isExpanded = this.planFilter.classList.contains('is-expanded');
    this.showAllPlansButton.textContent = isExpanded ?
      this.showAllPlansButton.dataset.collapseLabel :
      this.showAllPlansButton.dataset.expandLabel;

    this.showAllPlansButton.setAttribute('aria-expanded', isExpanded.toString());
  }

  bindYearFilter() {
    if (!this.yearFilter) {
      return;
    }

    this.yearFilterObserver = new MutationObserver(() => {
      this.updateStatementVisibility();
    });

    this.yearFilterObserver.observe(this.yearFilter, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true,
    });
  }

  selectAllPlans() {
    this.setAllPlansChecked(true);
  }

  applyInitialPlansFromUrl() {
    const params = new URLSearchParams(window.location.search);

    if (!params.has(this.planQueryParameter)) {
      this.selectAllPlans();
      return;
    }

    const activePlanIds = params.getAll(this.planQueryParameter)
      .reduce((planIds, value) => planIds.concat(value.split(',')), [])
      .map(planId => planId.trim())
      .filter(Boolean);
    this.planCheckboxes.forEach((checkbox) => {
      /* eslint-disable-next-line no-param-reassign */
      checkbox.checked = activePlanIds.indexOf(checkbox.value) !== -1;
    });
    this.syncAllPlansCheckbox();
  }

  setAllPlansChecked(checked) {
    this.allPlansCheckbox.checked = checked;
    this.planCheckboxes.forEach((checkbox) => {
      /* eslint-disable-next-line no-param-reassign */
      checkbox.checked = checked;
    });
  }

  syncAllPlansCheckbox() {
    const checkedPlansCount = this.planCheckboxes.filter(checkbox => checkbox.checked).length;

    this.allPlansCheckbox.checked = checkedPlansCount === this.planCheckboxes.length;
  }

  getActivePlanIds() {
    return this.planCheckboxes
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.value);
  }

  getStatementPlanIds(row) {
    const rawPlanIds = row.dataset.planIds || '';

    return rawPlanIds
      .split(',')
      .map(planId => planId.trim())
      .filter(Boolean);
  }

  updateUrlFromActivePlans() {
    const activePlanIds = this.getActivePlanIds();
    const params = new URLSearchParams(window.location.search);
    params.delete(this.planQueryParameter);

    const queryParts = [];
    const remainingQuery = params.toString();
    if (remainingQuery) {
      queryParts.push(remainingQuery);
    }

    if (activePlanIds.length < this.planCheckboxes.length) {
      const planIds = activePlanIds.map(planId => encodeURIComponent(planId)).join(',');
      queryParts.push(`${this.planQueryParameter}=${planIds}`);
    }

    const newQuery = queryParts.join('&');
    const newUrl = newQuery ? `${window.location.pathname}?${newQuery}` : window.location.pathname;
    window.history.replaceState(null, '', newUrl);
  }

  getActiveYears() {
    if (!this.yearFilter) {
      return [];
    }

    return Array.from(
      this.yearFilter.querySelectorAll('.category-filter.isActive:not([data-id^="select-all-"])'),
    ).map(filter => filter.dataset.label);
  }

  updateStatementVisibility() {
    const activePlanIds = this.getActivePlanIds();
    const activeYears = this.getActiveYears();

    this.statementRows.forEach((row) => {
      const rowPlanIds = this.getStatementPlanIds(row);

      row.classList.toggle(
        'is-hidden-by-plan-filter',
        !rowPlanIds.some(planId => activePlanIds.indexOf(planId) !== -1),
      );
      row.classList.toggle(
        'is-hidden-by-year-filter',
        activeYears.length > 0 && activeYears.indexOf(row.dataset.year) === -1,
      );
    });

    this.element.dispatchEvent(new CustomEvent('statements:filter-change'));
  }

  toggleAllPlans() {
    this.planFilter.classList.toggle('is-expanded');
    this.updateShowAllPlansButtonState();
  }
}

export default Statements;
